import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { ReelSummary } from '../api/types';
import { ensureShareLink } from '../api/rttsClient';
import {
  STUDIO_COPY,
  formatGenerateFailedCopy,
  isStudioJobInProgress,
  studioStatusLabel,
} from '../config';
import { formatDuration, formatVodDate, isJobExpired } from '../utils/format';
import {
  isReelNameValid,
  normalizeReelName,
  reelDisplayTitle,
  reelNeedsName,
} from '../utils/reelName';
import ReelDownloadButton from './ReelDownloadButton';
import ReelNameField from './ReelNameField';

type ReelCardProps = {
  reel: ReelSummary;
  onDelete: (jobId: string) => Promise<void>;
  onToast: (message: string) => void;
  onRename?: (jobId: string, reelName: string) => Promise<void>;
};

const ReelCard: React.FC<ReelCardProps> = ({ reel, onDelete, onToast, onRename }) => {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [posterBroken, setPosterBroken] = useState(false);
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const title = reelDisplayTitle(reel);
  const needsName = reelNeedsName(reel);
  const inProgress = isStudioJobInProgress(reel.status);
  const failed = reel.status === 'failed';
  const delivered = reel.status === 'delivered';
  const signedUrl = reel.signed_url ?? '';
  const shareUrl = reel.share_url ?? '';
  const expired =
    delivered && (!signedUrl || isJobExpired(reel.expires_at));
  const posterUrl =
    !posterBroken && reel.poster_url ? reel.poster_url : null;
  const durationSec =
    delivered && typeof reel.reel_duration_sec === 'number'
      ? reel.reel_duration_sec
      : null;
  const canRename = Boolean(onRename) && !needsName;
  const showNameEditor = Boolean(onRename && naming);

  const handleCopy = async () => {
    if (!delivered) return;
    try {
      let url = shareUrl;
      if (!url || !url.startsWith('https://scoutlytics.gg/r/')) {
        const minted = await ensureShareLink(reel.job_id);
        url = minted.share_url;
      }
      if (!url.startsWith('https://scoutlytics.gg/r/')) return;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(reel.job_id);
    } catch {
      onToast(STUDIO_COPY.reelsDeleteError);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const openNudge = () => {
    setDraftName('');
    setNaming(true);
  };

  const openRename = () => {
    const current = normalizeReelName(reel.reel_name);
    setDraftName(current || title);
    setNaming(true);
  };

  const handleSaveName = async () => {
    if (!onRename || !isReelNameValid(draftName)) return;
    setSavingName(true);
    try {
      await onRename(reel.job_id, normalizeReelName(draftName));
      setNaming(false);
    } catch {
      onToast(STUDIO_COPY.reelsRenameError);
    } finally {
      setSavingName(false);
    }
  };

  return (
    <article
      className="studio-reel-card"
      data-testid="studio-reel-card"
      data-job-id={reel.job_id}
      data-status={reel.status}
    >
      <div className="studio-reel-card-poster" aria-hidden={!posterUrl}>
        {posterUrl ? (
          <img
            src={posterUrl}
            alt={STUDIO_COPY.reelPosterAlt}
            className="studio-reel-card-poster-img"
            onError={() => setPosterBroken(true)}
          />
        ) : (
          <div
            className={`studio-reel-card-placeholder${
              inProgress ? ' studio-reel-card-placeholder--cutting' : ''
            }`}
            data-testid="studio-reel-card-placeholder"
          >
            <span className="studio-tape-stripe studio-reel-card-tape" />
          </div>
        )}
        {delivered && posterUrl ? (
          <span className="studio-reel-card-play" aria-hidden>
            <span className="studio-reel-card-play-icon">▶</span>
          </span>
        ) : null}
        {inProgress ? (
          <span
            className="studio-reel-status-badge studio-reel-status-badge--progress"
            data-testid="studio-reel-status-badge"
          >
            <span className="studio-spinner studio-spinner--sm" aria-hidden />
            {STUDIO_COPY.reelsCuttingBadge} {studioStatusLabel(reel.status)}
          </span>
        ) : null}
        {failed ? (
          <span
            className="studio-reel-status-badge studio-reel-status-badge--failed"
            data-testid="studio-reel-status-badge"
          >
            {STUDIO_COPY.reelsFailedBadge}
          </span>
        ) : null}
      </div>

      <div className="studio-reel-card-body">
        {showNameEditor ? (
          <div className="studio-reel-name-nudge" data-testid="studio-reel-rename-editor">
            <ReelNameField
              id={`studio-reel-name-${reel.job_id}`}
              value={draftName}
              onChange={setDraftName}
              showError
              disabled={savingName}
              label={needsName ? STUDIO_COPY.reelNameLabel : STUDIO_COPY.reelsRename}
              hideLabel={!needsName}
            />
            <div className="studio-reel-card-actions">
              <button
                type="button"
                className="studio-btn-primary studio-btn-compact"
                data-testid="studio-reel-name-save"
                disabled={savingName || !isReelNameValid(draftName)}
                onClick={() => void handleSaveName()}
              >
                {savingName
                  ? '…'
                  : needsName
                    ? STUDIO_COPY.reelsNameThisReelSave
                    : STUDIO_COPY.reelsRenameSave}
              </button>
              <button
                type="button"
                className="studio-btn-ghost studio-btn-compact"
                data-testid="studio-reel-rename-cancel"
                disabled={savingName}
                onClick={() => setNaming(false)}
              >
                {STUDIO_COPY.reelsRenameCancel}
              </button>
            </div>
          </div>
        ) : (
          <h2 className="studio-reel-card-title" data-testid="studio-reel-card-title">
            {title}
          </h2>
        )}
        {needsName && onRename && !naming ? (
          <div className="studio-reel-name-nudge" data-testid="studio-reel-name-nudge">
            <button
              type="button"
              className="studio-btn-ghost studio-btn-compact"
              data-testid="studio-reel-name-nudge-open"
              onClick={openNudge}
            >
              {STUDIO_COPY.reelsNameThisReel}
            </button>
          </div>
        ) : null}
        <p className="studio-reel-card-meta">
          <span data-testid="studio-reel-card-date">
            {formatVodDate(reel.created_at)}
          </span>
          {reel.vod_created_at ? (
            <>
              <span aria-hidden> · </span>
              <span data-testid="studio-reel-card-vod-date">
                {formatVodDate(reel.vod_created_at)}
              </span>
            </>
          ) : null}
          {typeof reel.clip_count === 'number' ? (
            <>
              <span aria-hidden> · </span>
              <span data-testid="studio-reel-card-clips">
                {STUDIO_COPY.clipCountTemplate.replace(
                  '{n}',
                  String(reel.clip_count)
                )}
              </span>
            </>
          ) : null}
          {durationSec != null ? (
            <>
              <span aria-hidden> · </span>
              <span data-testid="studio-reel-card-duration">
                {formatDuration(durationSec)}
              </span>
            </>
          ) : null}
        </p>
        {failed ? (
          <p
            className="studio-reel-card-error"
            data-testid="studio-reel-card-error"
          >
            {formatGenerateFailedCopy(reel.error_code)}
          </p>
        ) : null}

        <div className="studio-reel-card-actions">
          {delivered ? (
            <>
              <Link
                to={`/studio/reel/${encodeURIComponent(reel.job_id)}`}
                className="studio-btn-primary no-underline studio-btn-compact"
                data-testid="studio-reel-card-play"
              >
                Play
              </Link>
              {signedUrl && !expired ? (
                <ReelDownloadButton
                  url={signedUrl}
                  className="studio-btn-ghost no-underline studio-btn-compact"
                  testId="studio-reel-card-download"
                />
              ) : null}
              <button
                type="button"
                className="studio-btn-ghost studio-btn-compact"
                onClick={() => void handleCopy()}
                data-testid="studio-reel-card-copy"
              >
                {copied ? STUDIO_COPY.copiedConfirm : 'Copy link'}
              </button>
              {canRename && !naming ? (
                <button
                  type="button"
                  className="studio-btn-ghost studio-btn-compact"
                  data-testid="studio-reel-card-rename"
                  onClick={openRename}
                >
                  {STUDIO_COPY.reelsRename}
                </button>
              ) : null}
            </>
          ) : null}
          {inProgress ? (
            <Link
              to={`/studio/reel/${encodeURIComponent(reel.job_id)}`}
              className="studio-btn-ghost no-underline studio-btn-compact"
              data-testid="studio-reel-card-view-progress"
            >
              View progress
            </Link>
          ) : null}
          {failed ? (
            <button
              type="button"
              className="studio-btn-primary studio-btn-compact"
              data-testid="studio-reel-card-retry"
              onClick={() => {
                const retryName = isReelNameValid(reel.reel_name)
                  ? normalizeReelName(reel.reel_name)
                  : '';
                navigate(
                  `/studio/generate/${encodeURIComponent(reel.provider_vod_id)}`,
                  retryName ? { state: { reelName: retryName } } : undefined
                );
              }}
            >
              Retry
            </button>
          ) : null}

          {confirmDelete ? (
            <span className="studio-reel-delete-confirm" data-testid="studio-reel-delete-confirm">
              <span className="text-xs text-[var(--studio-muted)]">
                {STUDIO_COPY.reelsDeleteConfirm}
              </span>
              <button
                type="button"
                className="studio-btn-ghost studio-btn-compact"
                disabled={deleting}
                onClick={() => void handleDelete()}
                data-testid="studio-reel-delete-confirm-yes"
              >
                {deleting ? '…' : 'Delete'}
              </button>
              <button
                type="button"
                className="studio-btn-ghost studio-btn-compact"
                disabled={deleting}
                onClick={() => setConfirmDelete(false)}
                data-testid="studio-reel-delete-confirm-no"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="studio-btn-ghost studio-btn-compact studio-reel-delete"
              onClick={() => setConfirmDelete(true)}
              data-testid="studio-reel-card-delete"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default ReelCard;
